<template>
  <div class="w-full h-full overflow-x-hidden overflow-y-auto">
    <tx-collapse v-model="activeGroupList" class="mb-4">
      <tx-collapse-item v-for="(group, groupIndex) in refGroupedAttributes" :key="groupIndex" :title="group.label" :value="group.label" class="pb-4">
        <div class="flex flex-wrap w-full h-full">
          <!-- <div v-for="attribute in group.attributes" class="flex-grow flex-shrink" :class="[`basis-[${100 / columns}%] p-2`]"> -->
          <template v-for="attribute in group.attributes" :key="groupIndex + attribute.SystemName">
            <div
              v-if="!attributeOptions[attribute.SystemName].hide"
              class="p-2 mb-3 last-of-type:mb-2"
              :style="[attribute.InputType === 'tagsSelect' ? { flex: '100%' } : { flex: `${100 / columns}%` }]"
              :class="{ 'col-span-2': attribute.InputType && attribute.InputType === 'tagsSelect' }"
            >
              <attribute-editor
                v-model="form[attribute.SystemName]"
                v-model:checked="attributeOptions[attribute.SystemName].checked"
                :articles="[article]"
                :attribute="attribute"
                :form="form"
                :disabled="haveUpdatePrivilege || attribute.ReadOnly || !attribute.Editable || readOnly || attributeOptions[attribute.SystemName].disabled"
                :required="attribute.IsRequired || attributeOptions[attribute.SystemName].required"
                :show-checkbox="true"
                :show-external-change-management-info="attribute.ExternalChangeManagementURL != null"
                :show-obsolete-master-sizescale-warning="showObsoleteMasterSizescaleWarning"
                :errors="v$[attribute.SystemName].$errors"
                @blur="v$[attribute.SystemName].$touch"
                @check-changed="v$[attribute.SystemName].$touch"
                @change="handleChange(attribute)"
              />
            </div>
          </template>
        </div>
      </tx-collapse-item>
    </tx-collapse>
    <div class="flex flex-wrap w-full">
      <!-- <div v-for="attribute in  nonGroupedAttributes" class="flex-grow flex-shrink" :class="[`basis-[${100 / columns}%] p-2`]"> -->
      <template v-for="attribute in refNonGroupedAttributes" :key="attribute.SystemName">
        <div
          v-if="!attributeOptions[attribute.SystemName].hide"
          class="p-2 mb-3 last-of-type:mb-2"
          :style="[attribute.InputType === 'tagsSelect' ? { flex: '100%' } : { flex: `${100 / columns}%` }]"
          :class="{ 'col-span-2': attribute.InputType && attribute.InputType === 'tagsSelect' }"
        >
          <attribute-editor
            v-model="form[attribute.SystemName]"
            v-model:checked="attributeOptions[attribute.SystemName].checked"
            :articles="[article]"
            :attribute="attribute"
            :form="form"
            :disabled="haveUpdatePrivilege || attribute.ReadOnly || !attribute.Editable || readOnly || attributeOptions[attribute.SystemName].disabled"
            :required="attribute.IsRequired || attributeOptions[attribute.SystemName].required"
            :show-checkbox="true"
            :show-external-change-management-info="attribute.ExternalChangeManagementURL != null"
            :show-obsolete-master-sizescale-warning="showObsoleteMasterSizescaleWarning"
            :should-consider-obsolete-model-value="shouldConsiderObsoleteModelValue"
            :errors="v$[attribute.SystemName].$errors"
            @blur="v$[attribute.SystemName].$touch"
            @check-changed="v$[attribute.SystemName].$touch"
            @change="handleChange(attribute)"
            @reset-size-scale="resetSizeScale"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch, watchEffect } from 'vue'
import useAttributeValidator from '../composables/attributeValidator'
import AttributeEditor from '@/shared/components/AttributeEditor.vue'
import type MyArticle from '@/models/myArticle'
import TxCollapse from '@/shared/components/txCollapse/txCollapse.vue'
import TxCollapseItem from '@/shared/components/txCollapse/txCollapseItem.vue'
import utils from '@/services/utils'
import { AttributeType } from '@/models/catalogAttribute'
import { privileges } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import { useArticleFormHelper } from '@/shared/composables/articleFormHelper'

interface IProps {
  article: MyArticle
  activeGroups?: Array<string>
  attributes: IMyAttribute[]
  columns: number
  groupedAttributes?: Array<{ label: string, attributes: Array<IMyAttribute> }>
  nonGroupedAttributes?: Array<IMyAttribute>
  formConditionsConfig?: Record<string, IFormConditionConfig[]>
  readOnly?: boolean
  showObsoleteMasterSizescaleWarning?: boolean
  shouldConsiderObsoleteModelValue?: boolean
  liveDirtyFields?: string[]
}
const props = withDefaults(defineProps<IProps>(), {
  activeGroups: () => [] as Array<string>,
  attributes: () => [] as Array<IMyAttribute>,
  nonGroupedAttributes: () => ([]) as Array<IMyAttribute>,
  groupedAttributes: () => ([]) as Array<{ label: string, attributes: Array<IMyAttribute> }>,
  columns: 1,
  readOnly: false,
})

const emit = defineEmits<{
  (e: 'formDirtyStateChanged', isFormDirty: boolean): void
  (e: 'changed', form: Record<string, any>): void
}>()

const userStore = useUserStore()

const form = reactive<Record<string, any>>({})
const attributeOptions = reactive<Record<string, { checked: boolean, disabled: boolean, required: boolean, hide: boolean, isDirty: boolean }>>({})
const activeGroupList = ref(props.activeGroups)
const refGroupedAttributes = ref(props.groupedAttributes)
const refNonGroupedAttributes = ref(props.nonGroupedAttributes)
const refAttributes = ref(props.attributes)

const { updateCriteriaAttributeAllowedValues, updateLookupAttributeValues, updateSizeScaleCriteriaAllowedValues } = useArticleFormHelper()
const { v$ } = useAttributeValidator(ref([props.article]), props.attributes, form, attributeOptions)

const haveUpdatePrivilege = computed(() => !userStore.userProfile.isValidPrivilege(privileges.article.update))

// the will consider dirty if any of the checkboxes is selected
const isDirty = computed(() => {
  return Object.values(attributeOptions).some(attributeOptionsValue => attributeOptionsValue.checked)
})

const visibleAttributesMap = computed(() => {
  const map: Record<string, IMyAttribute> = {}
  refAttributes.value.forEach((attr) => {
    map[attr.SystemName] = attr
  })
  return map
})

watchEffect(() => {
  emit('formDirtyStateChanged', isDirty.value)
})

watch(attributeOptions, () => {
  emitCheckedValues()
})

watch(() => props.attributes, (attributes) => {
  refAttributes.value = attributes
  setRefAttributesValidationsAndCriterias()
})

watch(() => props.groupedAttributes, (groupedAttributes) => {
  refGroupedAttributes.value = groupedAttributes
})

watch(() => props.nonGroupedAttributes, (nonGroupedAttributes) => {
  refNonGroupedAttributes.value = nonGroupedAttributes
})

initForm()

function handleChange(attribute: IMyAttribute) {
  if (attributeOptions.hasOwnProperty(attribute.SystemName)) {
    v$.value[attribute.SystemName].$touch()
    attributeOptions[attribute.SystemName].checked = true
    attributeOptions[attribute.SystemName].isDirty = true

    const updatedAttributes = []
    updateCriteriaAttributeAllowedValues(refAttributes, form, form, updatedAttributes)
    const masterSizeScaleAttribute = refAttributes.value.find(field => field.SystemName === 'MasterSizeScaleId')
    if (masterSizeScaleAttribute && !masterSizeScaleAttribute.ReadOnly) {
      updateSizeScaleCriteriaAllowedValues(form, updatedAttributes)
    }
    updateLookupAttributeValues(refAttributes, form, form, updatedAttributes)
    updatedAttributes.forEach((lookUpAttribute) => {
      attributeOptions[lookUpAttribute].checked = true
      attributeOptions[lookUpAttribute].isDirty = true
    })

    updateAttributeOptions(attribute.SystemName, form[attribute.SystemName])

    emitCheckedValues()
  }
}

function updateAttributeOptions(systemName: string, attributeValue: any) {
  if (props.formConditionsConfig?.[systemName]) {
    const result = utils.evaluateFormConditions(props.formConditionsConfig[systemName], attributeValue, visibleAttributesMap.value)
    for (const key in result) {
      if (!attributeOptions.hasOwnProperty(key)) {
        attributeOptions[key] = { checked: false, disabled: false, required: false, hide: false, isDirty: false }
      }
      attributeOptions[key].required = result[key].required
      attributeOptions[key].disabled = result[key].disabled
      attributeOptions[key].hide = result[key].hide
    }
  }
}

function emitCheckedValues() {
  const checkedValues: Record<string, any> = {}
  props.attributes.forEach((attribute) => {
    if (attributeOptions[attribute.SystemName].checked && attributeOptions[attribute.SystemName].isDirty) {
      checkedValues[attribute.SystemName] = form[attribute.SystemName]
    }
  })
  emit('changed', checkedValues)
}

function resetSizeScale() {
  form.SizeScaleId = null
}

function initForm() {
  for (const key in form) {
    if (!props.liveDirtyFields || !props.liveDirtyFields.includes(key)) {
      delete form[key]
    }
  }
  for (const key in attributeOptions) {
    if (!props.liveDirtyFields || !props.liveDirtyFields.includes(key)) {
      delete attributeOptions[key]
    }
  }
  setRefAttributesValidationsAndCriterias()
}

function setRefAttributesValidationsAndCriterias() {
  props.attributes.forEach((attribute) => {
    if (!props.liveDirtyFields || !props.liveDirtyFields.includes(attribute.SystemName)) {
      let articleAttributeValue = utils.getArticleAttributeValue(attribute, props.article)
      const sourceFields: Record<string, any> = {}
      if (utils.isDefined(attribute.parsedValidationExpression) && attribute.parsedValidationExpression.length) {
        attribute.parsedValidationExpression.forEach((itm) => {
          if (utils.isDefined(itm.SourceField) && props.article.hasOwnProperty(itm.SourceField)) {
            sourceFields[itm.SourceField] = props.article[itm.SourceField]
          }
        })
      }
      if (!utils.isDefined(articleAttributeValue) && attribute.AttributeType === AttributeType.MultiValue && attribute.VettingList && attribute.VettingList.length > 0) {
        articleAttributeValue = []
      }
      form[attribute.SystemName] = articleAttributeValue
      attributeOptions[attribute.SystemName] = { checked: false, disabled: false, required: false, hide: false, isDirty: false }
    }
  })
  const updatedAttributes = []
  updateCriteriaAttributeAllowedValues(refAttributes, form, form, updatedAttributes)
  updateLookupAttributeValues(refAttributes, form, form, updatedAttributes)
  updatedAttributes.forEach((lookUpAttribute) => {
    attributeOptions[lookUpAttribute].checked = true
    attributeOptions[lookUpAttribute].isDirty = true
  })

  props.attributes.forEach((attribute) => {
    updateAttributeOptions(attribute.SystemName, form[attribute.SystemName])
  })
}

watch(() => [props.article, props.attributes], initForm)

function setForm(f: Record<string, any>) {
  for (const key in f) {
    form[key] = f[key]
    attributeOptions[key].checked = true
    attributeOptions[key].isDirty = true
  }
}

defineExpose({
  v$,
  setForm,
  updateAttributeOptions,
})
</script>
